import { Animated, PixelRatio, Platform } from 'react-native';
import {
  AvailabilityMap,
  ItemListOption,
  ItemListOptionMetadata,
  VariantPackMetadata,
} from '@/contexts/item-context-types';
import {
  BrandPageLinkFieldsFragment,
  ItemContext_SaltItemFieldsFragment,
  ItemDetail_GtinMetadataFieldsFragment,
  ItemDetail_OrderHistoryFieldsFragment,
} from '@/types';
import { ItemInformationConfig } from '@/components/orders/item-detail-metadata-link';
import { LIST_HEIGHT } from '@/components/horizontal-selector';
import { ListOption } from '@/components/list-item';
import { RenderActionsFn } from '@/components/orders/item-detail-actions';
import { TYPOGRAPHY } from '@/styles';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import { useEffect, useRef } from 'react';
import { useLayoutContext } from '@/contexts/layout-context';

export interface PositionType {
  startPosition: number;
}

export type HeaderListOption = ListOption<PositionType>;

export interface Props {
  availableQuantityMap?: AvailabilityMap;
  brandPageDetails?: BrandPageLinkFieldsFragment | null;
  gtinMetadata: ItemDetail_GtinMetadataFieldsFragment | null;
  inDraggableSheet?: boolean;
  isSkuInCartOrOrderGuide: (sku: ItemListOptionMetadata) => Boolean;
  itemInformation: ItemInformationConfig | null;
  ItemMenuButton: React.ReactNode;
  onChangeSelector: () => void;
  onClose: () => void;
  onSelectItem: (item: ItemListOption, source: string) => void;
  orderHistoryItems: ItemDetail_OrderHistoryFieldsFragment[];
  orderHistoryLoading: boolean;
  renderActions?: RenderActionsFn;
  saltItemInfo?: ItemContext_SaltItemFieldsFragment | null;
  selectedItem: ItemListOption;
  setDSRToolingActionSheetItemMetadata: (sku: ItemListOptionMetadata) => void;
  setShowTitle: (nextValue: boolean) => void;
  showSelector: boolean;
  source: string;
  updateQuantity: UpdateQuantityFn;
  variantPackMetadata: VariantPackMetadata | null;
}

// decrease margin to account for drag handle on native
const ACTION_SHEET_MARGIN_TOP = Platform.select({
  default: 22,
  web: 32,
});

export const useItemDetailSheetMarginTop = () => {
  const { isSmallLayout } = useLayoutContext();
  return isSmallLayout ? ACTION_SHEET_MARGIN_TOP : 0;
};

const ANIMATION_TIMING = 150;

export const useRenderInDraggableSheet = () => {
  const { isSmallLayout } = useLayoutContext();
  return isSmallLayout && Platform.OS !== 'web';
};

const fontScale = PixelRatio.getFontScale();

export const TITLE_PADDING_TOP = 16;

export const useItemDetailTitleMaxHeight = () => {
  const isInDraggableSheet = useRenderInDraggableSheet();
  const { isSmallLayout, isLargeLayout } = useLayoutContext();

  // large layout title is not absolutely positioned
  if (isLargeLayout) {
    return 0;
  }

  const titleTextHeight = isSmallLayout
    ? TYPOGRAPHY.medium.lineHeight * fontScale
    : 0;

  const extraPadding = isInDraggableSheet ? 0 : TITLE_PADDING_TOP;

  return LIST_HEIGHT + titleTextHeight + extraPadding;
};

export const useItemDetailTitleAnimation = (
  isVisible: boolean,
  onVisibilityChange?: (nextValue: boolean) => void,
) => {
  const headerOpacityRef = useRef(new Animated.Value(0));
  const headerTranslateYRef = useRef(new Animated.Value(0));

  useEffect(() => {
    if (isVisible) {
      onVisibilityChange?.(true);
    }

    Animated.parallel([
      Animated.timing(headerOpacityRef.current, {
        duration: ANIMATION_TIMING,
        toValue: isVisible ? 1 : 0,
        useNativeDriver: true,
      }),
      Animated.timing(headerTranslateYRef.current, {
        duration: ANIMATION_TIMING,
        toValue: isVisible ? 0 : -40,
        useNativeDriver: true,
      }),
    ]).start(() => {
      if (!isVisible) {
        onVisibilityChange?.(false);
      }
    });
  }, [isVisible, onVisibilityChange]);

  return {
    headerOpacityRef,
    headerTranslateYRef,
  };
};
